import React, { useState, useEffect } from 'react';
import { AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const Reset = () => {
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverStatus, setServerStatus] = useState('checking');

  useEffect(() => {
    const checkServer = async () => {
      try {
        await axios.get('/api/health');
        setServerStatus('online');
      } catch (error) {
        setServerStatus('offline');
      }
    };
    checkServer();
  }, []);

  const dataToReset = [
    'All customer records and vehicle information',
    'All job cards and service history',
    'All estimates, quotations, and invoices',
    'All payments and accounting transactions',
    'All appointments and schedules',
    'All vendor and purchase order data',
    'All user accounts and logs'
  ];
  
  const dataToPreserve = [
    'Parts Management (quantities reset to 0)',
    'Services Master data',
    'Vehicle Makes and Models registry'
  ];

  const handleFixRBAC = async () => {
    try {
      const response = await axios.get('/api/rbac/setup-rbac');
      toast.success('RBAC permissions fixed successfully!');
    } catch (error) {
      toast.error('Failed to fix RBAC: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // Try the database reset endpoint first
      const response = await axios.post('/api/database/reset', { password: form.password });
      
      if (response.data.success) {
        toast.success('System reset complete! Database restored to clean state.');
        toast.success('Admin login: umarfarooq1102@gmail.com / Umar@8627', { duration: 8000 });
        
        // Fix RBAC automatically after reset
        try {
          const rbacResponse = await axios.get('/api/rbac/setup-rbac');
          toast.success('RBAC permissions restored with 101 permissions!');
        } catch (rbacError) {
          toast.error('RBAC setup failed - you may need to run: node fix-rbac.js');
        }
        
        setForm({ password: '', confirmPassword: '' });
        setShowConfirm(false);
        
        // Clear auth and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    } catch (error) {
      // Check if it's a network error
      if (!error.response) {
        toast.error('Cannot connect to server. Please check if the server is running.');
        return;
      }
      
      // Check for specific error codes
      if (error.response.status === 401) {
        toast.error('Unauthorized. Please login again.');
        return;
      }
      
      if (error.response.status === 400) {
        toast.error(error.response.data?.error || 'Invalid password');
        return;
      }
      
      // Try organization reset as fallback
      try {
        const orgResponse = await axios.post('/api/organization/reset', { password: form.password });
        
        if (orgResponse.data.success) {
          toast.success('System reset complete! Database restored to clean state.');
          toast.success('Admin login: umarfarooq1102@gmail.com / Umar@8627', { duration: 8000 });
          
          // Fix RBAC automatically after reset
          try {
            const rbacResponse = await axios.get('/api/rbac/setup-rbac');
            toast.success('RBAC permissions restored with 101 permissions!');
          } catch (rbacError) {
            toast.error('RBAC setup failed - you may need to run: node fix-rbac.js');
          }
          
          setForm({ password: '', confirmPassword: '' });
          setShowConfirm(false);
          
          // Clear auth and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        }
      } catch (orgError) {
        const errorMessage = orgError.response?.data?.error || error.response?.data?.error || 'Failed to reset system';
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Reset System</h2>

      <div className="bg-white rounded-lg shadow p-6">
        {/* Server Status */}
        <div className={`flex items-center p-3 rounded-lg mb-4 ${
          serverStatus === 'online' ? 'bg-green-50 border border-green-200' :
          serverStatus === 'offline' ? 'bg-red-50 border border-red-200' :
          'bg-yellow-50 border border-yellow-200'
        }`}>
          {serverStatus === 'online' ? (
            <Wifi className="w-4 h-4 text-green-600 mr-2" />
          ) : serverStatus === 'offline' ? (
            <WifiOff className="w-4 h-4 text-red-600 mr-2" />
          ) : (
            <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin mr-2"></div>
          )}
          <span className={`text-sm font-medium ${
            serverStatus === 'online' ? 'text-green-800' :
            serverStatus === 'offline' ? 'text-red-800' :
            'text-yellow-800'
          }`}>
            Server Status: {serverStatus === 'online' ? 'Connected' : serverStatus === 'offline' ? 'Disconnected' : 'Checking...'}
          </span>
        </div>

        {/* Warning */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800 mb-2">
                Warning: This action cannot be undone
              </h3>
              <p className="text-sm text-red-700">
                Resetting your system will permanently delete all data. Please ensure you have 
                backed up any important information before proceeding.
              </p>
            </div>
          </div>
        </div>

        {/* Data to be affected */}
        <div className="mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-3 text-red-700">Data to be deleted:</h3>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <ul className="space-y-2">
                  {dataToReset.map((item, index) => (
                    <li key={index} className="flex items-center text-sm text-red-700">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-3 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3 text-green-700">Master data preserved:</h3>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <ul className="space-y-2">
                  {dataToPreserve.map((item, index) => (
                    <li key={index} className="flex items-center text-sm text-green-700">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Reset outcome */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-blue-700">After reset:</h3>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <ul className="space-y-2 text-sm text-blue-700">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0"></span>
                Default Admin user created (umarfarooq1102@gmail.com / Umar@8627)
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0"></span>
                Main Branch and AGMS organization setup
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0"></span>
                Default system settings configured
              </li>
            </ul>
          </div>
        </div>

        {/* Reset Form */}
        <form onSubmit={handleReset} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enter your password to confirm *
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm password *
            </label>
            <input
              type="password"
              value={form.confirmPassword}
              onChange={(e) => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="confirm"
              checked={showConfirm}
              onChange={(e) => setShowConfirm(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="confirm" className="text-sm text-gray-700">
              I understand that this action cannot be undone and will delete all data
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !showConfirm || !form.password || !form.confirmPassword || serverStatus !== 'online'}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold mb-3"
          >
            {loading ? 'Resetting System...' : serverStatus === 'offline' ? 'Server Offline' : '🔄 Reset System to Clean State'}
          </button>
          

        </form>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            This action requires administrator privileges and cannot be undone.
          </p>
          <p className="text-xs text-blue-600 mt-1">
            After reset, use umarfarooq1102@gmail.com / Umar@8627 to login
          </p>
        </div>
      </div>
    </div>
  );
};

export default Reset;